import React, { MouseEvent, useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Typography, CircularProgress } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';

import {
  VideoCameraOnIcon,
  VideoCameraOffIcon,
  VideoCameraDisableIcon,
} from '@icons';
import { ReactComponent as ScreenShareIcon } from '@public/images/camera/screen.svg';
import { ReactComponent as OnFrontAvatarIcon } from '@public/images/camera/avatar.svg';

import { useMytaverseEvent } from '@core/dashboard/providers';
import { useConference } from '../../../../DashboardContent/Dolby';
import { sendCustomSentryBreadcrumb } from '@helpers/sentry';
import { getCatchErrorMessage } from '@helpers/error';
import { useClientSpecificSDScreenShareButtonDisabled } from '@hooks/clientSpecificUtils';
import { useCommunications } from '@core/communications/providers/CommunicationsProvider';
import { AnalyticsType } from '@interfaces/analytics';
import ScrollableContainer from '@components/ScrollableContainer';
import SwipeableDrawer from '@core/dashboard/components/SwipeableDrawer';
import {
  Header,
  ModalContainer,
} from '@core/dashboard/components/PopoverModal';
import HomeBarButton from '@core/dashboard/components/HomeBarButton';
import useShareWebcam from './hooks';
import VideoComponent from '../VideoComponent';
import StyledBadge from './SyledBadge';
import { IProps } from './interfaces';
import { useStyles } from './styles';

enum ShareOptions {
  OnFrontAvatar = 'OnFrontAvatar',
  OnScreen = 'OnScreen',
}

const ShareWebcamButton = ({
  cameraDeviceId,
  onVideoClick,
  showWarningBadge,
}: IProps) => {
  const [open, setOpen] = useState(false);
  const [sharingOption, setSharingOption] = useState<ShareOptions>(
    ShareOptions.OnFrontAvatar,
  );
  const [isStartingBroadcast, setIsStartingBroadcast] = useState(false);

  const { t: translate } = useTranslation();

  const { ueWebcamScreenName, ueWebcamMediaStreams, isConferenceListener } =
    useConference();

  const {
    startShareCameraToScreen,
    stopShareCameraToScreen,
    isCameraOn,
    user,
    isJoiningConference,
    isCameraPermissionGranted,
  } = useCommunications();

  const { canSpeak, trackEventAnalytics, canShowCamera, canShareCamera } =
    useMytaverseEvent();

  const { currentCameraSharing } = useShareWebcam();

  const classes = useStyles();

  const isCameraButtonDisabled = useClientSpecificSDScreenShareButtonDisabled();

  useEffect(() => {
    if (canShareCamera !== canShowCamera) {
      if (canShareCamera) {
        setSharingOption(ShareOptions.OnScreen);
      } else if (canShowCamera) {
        setSharingOption(ShareOptions.OnFrontAvatar);
      }
    }
  }, [canShareCamera, canShowCamera]);

  useEffect(() => {
    // this forces the sharing option to be set back to "On Front Avatar", in case
    // the user has preselected "on Screen" and another user of the same room has
    // started to share its screen (2 users can't share their webcam on screen at the same time)

    if (
      ueWebcamMediaStreams.length &&
      !currentCameraSharing &&
      sharingOption === ShareOptions.OnScreen
    ) {
      setSharingOption(ShareOptions.OnFrontAvatar);
    }
  }, [ueWebcamMediaStreams]);

  const handleShareOnFrontAvatarClick = React.useCallback(async () => {
    setIsStartingBroadcast(true);
    try {
      await onVideoClick();

      setOpen((prevState) => {
        if (prevState) {
          return !prevState;
        }

        return prevState;
      });
    } catch (error: unknown) {
      throw Error(getCatchErrorMessage(error));
    } finally {
      setIsStartingBroadcast(false);
    }
  }, [onVideoClick]);

  const handleShareOnScreenClick = React.useCallback(async () => {
    try {
      if (ueWebcamScreenName) {
        setIsStartingBroadcast(true);

        if (user?.bVideoOn && ueWebcamMediaStreams.length !== 0) {
          sendCustomSentryBreadcrumb({
            message: 'Stop zoom share webcam to UE',
          });
          console.log('Stop zoom share webcam to UE');
          await stopShareCameraToScreen();
          trackEventAnalytics(AnalyticsType.CameraShareFinish);
        } else {
          sendCustomSentryBreadcrumb({
            message: 'Start zoom share webcam to UE',
          });
          console.log('Start zoom share webcam to UE');
          await startShareCameraToScreen();
          trackEventAnalytics(AnalyticsType.CameraShareStart);

          setOpen(false);
        }
      }
    } catch (error: unknown) {
      if (canSpeak) {
        //showNotification(getDolbyNotification());
      }
      throw Error(getCatchErrorMessage(error));
    } finally {
      setIsStartingBroadcast(false);
    }
  }, [
    ueWebcamScreenName,
    ueWebcamMediaStreams,
    currentCameraSharing,
    canSpeak,
    user,
    trackEventAnalytics,
  ]);

  const isScreenSharing =
    ueWebcamMediaStreams.length !== 0 && currentCameraSharing;

  const isFrontAvatarOn =
    isCameraOn && !!cameraDeviceId && !isConferenceListener;

  const isStreaming = isScreenSharing || isFrontAvatarOn;

  const handleToggleSharingVideo = React.useCallback(async () => {
    switch (sharingOption) {
      case ShareOptions.OnFrontAvatar:
        await handleShareOnFrontAvatarClick();
        break;
      case ShareOptions.OnScreen:
        await handleShareOnScreenClick();
        break;
    }
  }, [sharingOption, handleShareOnFrontAvatarClick, handleShareOnScreenClick]);

  const handleSharingButtonClick = React.useCallback(
    async (event: MouseEvent<HTMLButtonElement>) => {
      event.currentTarget.blur();

      trackEventAnalytics(AnalyticsType.NavbarButtonClick, { name: 'camera' });

      if (!cameraDeviceId) {
        return;
      }

      if (isStreaming) {
        await handleToggleSharingVideo();
      } else if (cameraDeviceId) {
        setOpen((prevState) => !prevState);
      }
    },
    [cameraDeviceId, isStreaming, handleToggleSharingVideo, trackEventAnalytics],
  );

  const renderButtonIcon = () => {
    if (isStartingBroadcast) {
      return <CircularProgress sx={classes.progress} />;
    }

    if (isStreaming) {
      return (
        <StyledBadge
          overlap="circular"
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          variant="dot"
        >
          <VideoCameraDisableIcon />
        </StyledBadge>
      );
    }

    if (open) {
      return <VideoCameraOnIcon />;
    }

    return showWarningBadge(<VideoCameraOffIcon />);
  };

  const showDolbyShareWebcam = !!ueWebcamScreenName;
  const showBothWebcamActions = canShareCamera && canShowCamera;

  const translatedTooltip =
    cameraDeviceId || isCameraPermissionGranted
      ? 'tooltips.toggleCam'
      : 'tooltips.warningPermissionCam';
  return (
    <>
      <HomeBarButton
        label={translate(translatedTooltip)}
        icon={renderButtonIcon()}
        isActive={open || isStreaming}
        handleClick={handleSharingButtonClick}
        disabled={!cameraDeviceId || isCameraButtonDisabled}
        isLoading={isJoiningConference}
      />
      <SwipeableDrawer
        position={{
          vertical: isMobileOnly ? 'top' : 'bottom',
          horizontal: 'left',
        }}
        open={open}
        onClose={handleSharingButtonClick}
        onOpen={handleSharingButtonClick}
      >
        <ModalContainer
          isScrollable={false}
          header={
            <Header
              text={translate('webcamShare.title')}
              onClose={() => setOpen(false)}
              isBig
            />
          }
        >
          <ScrollableContainer
            sxProps={{
              root: classes.scrollable(
                !isJoiningConference && showDolbyShareWebcam,
              ),
            }}
          >
            <Box sx={classes.content}>
              <VideoComponent cameraDeviceId={cameraDeviceId} />
              {!isJoiningConference &&
              showDolbyShareWebcam &&
              showBothWebcamActions ? (
                <Box sx={classes.actions}>
                  <IconButton
                    sx={classes.button(
                      sharingOption === ShareOptions.OnFrontAvatar,
                    )}
                    onClick={() => setSharingOption(ShareOptions.OnFrontAvatar)}
                    disabled={
                      !cameraDeviceId || currentCameraSharing || isScreenSharing
                    }
                    disableRipple
                  >
                    <OnFrontAvatarIcon />
                    <Typography sx={classes.buttonText}>
                      {translate('webcamShare.onFrontAvatar')}
                    </Typography>
                  </IconButton>

                  <IconButton
                    sx={classes.button(sharingOption === ShareOptions.OnScreen)}
                    onClick={() => setSharingOption(ShareOptions.OnScreen)}
                    disabled={
                      (ueWebcamMediaStreams.length !== 0 &&
                        !currentCameraSharing) ||
                      isFrontAvatarOn
                    }
                    disableRipple
                  >
                    <ScreenShareIcon />
                    <Typography sx={classes.buttonText}>
                      {translate('webcamShare.onScreen')}
                    </Typography>
                  </IconButton>
                </Box>
              ) : null}

              <LoadingButton
                onClick={handleToggleSharingVideo}
                disabled={isStartingBroadcast}
                loading={isStartingBroadcast}
                sx={classes.starStopButton}
              >
                <Typography>
                  {translate(
                    isStreaming
                      ? 'webcamShare.stopSharing'
                      : 'webcamShare.startSharing',
                  )}
                </Typography>
              </LoadingButton>
            </Box>
          </ScrollableContainer>
        </ModalContainer>
      </SwipeableDrawer>
    </>
  );
};

export default ShareWebcamButton;
